import { useContext, useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import usuariosService from 'services/usuarios';
import { AuthContext } from 'context';
import { RUTAS } from 'const/Rutas';
import { AlertaExito, AlertaError } from 'utils/Alertas';

export const useLegalAcceptance = (mode = 'forgot', { onAccepted } = {}) => {
  const { logeduser, token, refrescarSesion, logout } = useContext(AuthContext);
  const history = useHistory();

  const [open, setOpen] = useState(() => {
    if (mode === 'home') return !!(logeduser && !logeduser.aceptacionLegal);
    return true;
  });
  const [isAccepting, setIsAccepting] = useState(false);
  const [values, setValues] = useState({
    terminosYCondiciones: false,
    consentimientoDatos: false
  });
  // Estado de proceso :  `isProcessing` se usara como nombre claro
  // para indicar que hay una operación en curso. Internamente el hook
  // mantiene `isAccepting` para la lógica concreta de la aceptación, pero
  // hacia fuera exponemos `isProcessing` como alias para que los
  // componentes consumidores tengan un nombre más intuitivo.
  // Nota: se eliminó el antiguo `isSubmiting` porque no se estaba usando.

  useEffect(() => {
    if (mode === 'home' && logeduser) {
      setOpen(!logeduser.aceptacionLegal);
    }
  }, [logeduser, mode]);

  const handleClose = useCallback(() => setOpen(false), []);

  const handleChange = useCallback(event => {
    const { name, checked } = event.target;
    setValues(prev => ({ ...prev, [name]: checked }));
  }, []);

  const puedeContinuar =
    values.terminosYCondiciones && values.consentimientoDatos;

  const handleLogout = useCallback(async () => {
    try {
      logout();
      history.replace(RUTAS.HOME);
    } catch (err) {
      console.error('Error al cerrar sesión', err);
      history.replace(RUTAS.HOME);
    }
  }, [logout, history]);

  const handleAccept = useCallback(async () => {
    if (isAccepting) return; // prevención de doble envío: si ya está en curso, no hacer nada
    setIsAccepting(true);
    try {
      const result = await usuariosService.aceptarLegal(
        logeduser.codigoUsuario
      );
      if (result && result.status === 200) {
        await refrescarSesion(token);
        AlertaExito(
          'Tu perfil se actualizó correctamente: aceptaste los Términos y Condiciones, la Política de Privacidad y el Consentimiento Expreso e Informado.'
        );
        setOpen(false);
        if (typeof onAccepted === 'function') onAccepted();
      } else {
        throw new Error('Respuesta inesperada del servidor');
      }
    } catch (error) {
      console.error('Error al guardar aceptación', error);
      AlertaError(
        'Ha ocurrido un error al tratar de actualizar tu perfil con la aceptación de Términos y Condiciones, Política de Privacidad y el Consentimiento Expreso e Informado. Por favor inténtelo más tarde.'
      );
    } finally {
      setIsAccepting(false);
    }
  }, [isAccepting, logeduser, refrescarSesion, token, onAccepted]);

  return {
    open,
    handleClose,
    handleAccept,
    values,
    handleChange,
    puedeContinuar,
    handleLogout,
    isProcessing: isAccepting
  };
};

export default useLegalAcceptance;
